import { useMemo } from 'react';
import { useIntegration } from '@/providers/IntegrationProvider';
import { getIntegrationService } from '@/services/integration-service-registry';
import type { IntegrationService, SupportedIntegrationType } from '@/services/integration-service.interface';

// Re-export the IntegrationService type for backwards compatibility
export type { IntegrationService } from '@/services/integration-service.interface';

/**
 * Integration type that can be null when no integration is enabled
 */
export type IntegrationType = SupportedIntegrationType | null;

export interface UseIntegrationServiceResult {
  service: IntegrationService;
  integrationType: IntegrationType;
  isEnabled: boolean;
  isLoading: boolean;
}

/**
 * Hook to get the appropriate integration service based on the current integration settings.
 * 
 * This hook uses the integration service registry to dynamically select the correct
 * service implementation based on the active integration type (Financials, Dimensions,
 * Xero, QuickBooks, etc.)
 * 
 * @returns Object containing the service, integration type, enabled status, and loading state
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { service, integrationType, isEnabled, isLoading } = useIntegrationService();
 *   
 *   useEffect(() => {
 *     if (!isLoading && isEnabled) {
 *       service.getProjects().then(projects => {
 *         // Handle projects from the active integration
 *       });
 *     }
 *   }, [service, isEnabled, isLoading]);
 * }
 * ```
 */
export function useIntegrationService(): UseIntegrationServiceResult {
  const { settings, isLoading } = useIntegration();

  const integrationType: IntegrationType = useMemo(() => {
    if (!settings?.enabled) return null;
    // Return the integration type as-is if it's a supported type
    // The registry will handle fallback for unknown types
    return settings.integrationType as SupportedIntegrationType;
  }, [settings]);

  const service: IntegrationService = useMemo(() => {
    // Use the registry to get the appropriate service
    // The registry handles fallback to financials service for unknown types
    return getIntegrationService(integrationType);
  }, [integrationType]);

  const isEnabled = useMemo(() => {
    return settings?.enabled ?? false;
  }, [settings]);

  return {
    service,
    integrationType,
    isEnabled,
    isLoading,
  };
}
